import { environment } from '../../../environments/environment';

export type AppRole =
  | 'ROLE_USER'
  | 'ROLE_TENANT'
  | 'ROLE_LANDLORD'
  | 'ROLE_MANAGER'
  | 'ROLE_PROPERTY_MANAGER'
  | 'ROLE_ADMIN'
  | 'ROLE_SUPER_ADMIN'
  | 'ROLE_MAINTENANCE'
  | 'ROLE_MAINTENANCE_COMPANY';

const ROLE_PRIORITY: AppRole[] = [
  'ROLE_SUPER_ADMIN',
  'ROLE_ADMIN',
  'ROLE_LANDLORD',
  'ROLE_PROPERTY_MANAGER',
  'ROLE_MANAGER',
  'ROLE_MAINTENANCE',
  'ROLE_MAINTENANCE_COMPANY',
  'ROLE_TENANT',
  'ROLE_USER',
];

export function pickDashboardFor(roles: AppRole[] | null | undefined): string {
  if (!roles?.length) {
    return `${environment.publicAppUrl}/login`;
  }

  const portals = new Set(roles.map(role => role === 'ROLE_TENANT' ? 'tenant' : role === 'ROLE_MAINTENANCE' || role === 'ROLE_MAINTENANCE_COMPANY' ? 'maintenance' : ['ROLE_LANDLORD', 'ROLE_PROPERTY_MANAGER', 'ROLE_MANAGER'].includes(role) ? 'backoffice' : null).filter(Boolean));
  if (portals.size > 1 && !roles.includes('ROLE_ADMIN') && !roles.includes('ROLE_SUPER_ADMIN')) return `${environment.publicAppUrl}/select-workspace`;

  for (const role of ROLE_PRIORITY) {
    if (!roles.includes(role)) {
      continue;
    }

    switch (role) {
      case 'ROLE_MAINTENANCE_COMPANY': {
        // An outside maintenance company: its own pages in the maintenance portal (no workspace dashboard).
        return `${environment.maintenanceAppUrl}/company`;
      }

      case 'ROLE_TENANT': {
        return `${environment.tenantAppUrl}/dashboard`;
      }

      case 'ROLE_SUPER_ADMIN':
      case 'ROLE_ADMIN': {
        return `${environment.platformAdminAppUrl}/dashboard`;
      }

      case 'ROLE_MAINTENANCE': {
        return `${environment.maintenanceAppUrl}/dashboard`;
      }

      case 'ROLE_LANDLORD':
      case 'ROLE_PROPERTY_MANAGER':
      case 'ROLE_MANAGER': {
        return `${environment.backofficeAppUrl}/dashboard`;
      }

      case 'ROLE_USER': {
        return `${environment.publicAppUrl}/login`;
      }
    }
  }

  return `${environment.publicAppUrl}/login`;
}
export function isUrlAllowedForRoles(
  url: string | null | undefined,
  roles: AppRole[] | null | undefined,
): boolean {
  if (!url || !roles?.length) {
    return false;
  }

  if (!/^\/(?!\/)/.test(url)) {
    return false;
  }

  if (
    roles.includes('ROLE_SUPER_ADMIN') ||
    roles.includes('ROLE_ADMIN') ||
    roles.includes('ROLE_LANDLORD') ||
    roles.includes('ROLE_PROPERTY_MANAGER') ||
    roles.includes('ROLE_MANAGER')
  ) {
    return (
      url.startsWith('/dashboard') ||
      url.startsWith('/organizations') ||
      url.startsWith('/properties') ||
      url.startsWith('/staff') ||
      url.startsWith('/tenants') ||
      url.startsWith('/subscription')
    );
  }

  if (roles.includes('ROLE_TENANT')) {
    return url.startsWith('/dashboard') || url.startsWith('/tenant');
  }

  if (roles.includes('ROLE_MAINTENANCE')) {
    return (
      url.startsWith('/dashboard') ||
      url.startsWith('/work-orders') ||
      url.startsWith('/schedule') ||
      url.startsWith('/inspections') ||
      url.startsWith('/parts') ||
      url.startsWith('/reports') ||
      url.startsWith('/settings')
    );
  }

  return false;
}
